import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Alert,
  useTheme,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { adminColors, radius } from '../../theme/adminTheme';

// One-time temporary-password reveal — identical between StudentDrawer and
// StaffDrawer (each owns its own performResetPassword/copy-to-clipboard
// state and calls onResetPassword itself; this only renders the result).
// Extracted so the "not dismissable until copied" guard and clipboard
// fallback messaging can't drift between the two.
//
// `open` is derived from `temporaryPassword` being set, same as both
// drawers' own `Boolean(resetPasswordResult)` did before this was pulled
// out — there's no separate open/closed state to pass.
export default function ResetPasswordDialog({ username, temporaryPassword, hasEmail, copied, onCopy, onClose }) {
  const theme = useTheme();
  const open = Boolean(temporaryPassword);
  return (
    <Dialog
      open={open}
      onClose={copied ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      aria-labelledby="drawer-reset-password-result-title"
    >
      <DialogTitle id="drawer-reset-password-result-title" sx={{ fontWeight: 700 }}>
        Temporary password generated
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: adminColors.inkSoft, fontSize: '0.9rem', lineHeight: 1.6, mb: 2 }}>
          Share this with {username} securely. They should change it after signing in.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: adminColors.surface,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: radius.control,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <Typography
            component="code"
            sx={{
              fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
              fontSize: '0.95rem',
              color: adminColors.ink,
              userSelect: 'all',
              wordBreak: 'break-all',
            }}
          >
            {temporaryPassword}
          </Typography>
          <Tooltip title={copied ? 'Copied' : 'Copy'}>
            <IconButton
              size="small"
              onClick={onCopy}
              aria-label="Copy temporary password"
              sx={{ color: copied ? '#16a34a' : adminColors.accent, flexShrink: 0 }}
            >
              {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Alert severity="warning" variant="outlined">
          This password cannot be shown again after you close this dialog.
        </Alert>
        {!hasEmail && (
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
            Tip: add an email to enable self-service reset.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
